'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Sparkles, Mail, BarChart3, ClipboardList, Boxes, Brain, LineChart, Calendar, Megaphone, PenLine, Headphones, MessageCircle, Hash, CreditCard } from 'lucide-react';

export const dynamic = 'error'; // static marketing page
// NOTE: this page is client-side for the category filter. Metadata moves to layout if SEO needs.

// Fallback showcase — used when no API / backend available.
// IMPORTANT (feedback rule no_fake_data): these are illustrative archetypes, flagged clearly.
// Before GA, replace with real agents flagged public in the marketplace DB, OR hide section.
//
// Prices read "Coming soon" deliberately. This array previously carried
// invented dollar amounts ("$20/mo (utility)", "$35/mo (specialist)",
// "$50/mo (senior)", "Included in Pro+") on a public marketing page for
// products that have no pricing. Names/roles/icons stay so the grid still
// communicates the archetypes. Put real numbers here only when the
// marketplace DB is wired and the prices are real.
const SHOWCASE_AGENTS = [
  { id: 'chief-of-staff',        name: 'Chief of Staff',        role: 'General orchestration',        price: 'Coming soon',       category: 'general',  archetype: true, icon: Brain },
  { id: 'email-triage',          name: 'Email Triage',          role: 'Inbox management & replies',   price: 'Coming soon',       category: 'ops',      archetype: true, icon: Mail },
  { id: 'research-analyst',      name: 'Research Analyst',      role: 'Deep web + synthesis',         price: 'Coming soon',    category: 'research', archetype: true, icon: Brain },
  { id: 'sales-lead',            name: 'Sales Lead',            role: 'Pipeline coordination',        price: 'Coming soon',        category: 'sales',    archetype: true, icon: LineChart },
  { id: 'appointment-coordinator', name: 'Appointment Coordinator', role: 'Booking & calendar',        price: 'Coming soon',    category: 'ops',      archetype: true, icon: Calendar },
  { id: 'content-writer',        name: 'Content Writer',        role: 'Blog / email / social copy',   price: 'Coming soon',    category: 'content',  archetype: true, icon: PenLine },
  { id: 'customer-support',      name: 'Customer Support',      role: 'Tier-1 ticket triage + reply', price: 'Coming soon',    category: 'ops',      archetype: true, icon: Headphones },
  { id: 'social-manager',        name: 'Social Manager',        role: 'Post scheduling + reply watch', price: 'Coming soon',   category: 'content',  archetype: true, icon: Megaphone },
] as const;

const SHOWCASE_SKILLS = [
  { id: 'quickbooks',  name: 'QuickBooks',         short: 'Reconcile transactions, generate P&L reports, track categories.',      icon: BarChart3 },
  { id: 'gmail',       name: 'Gmail',              short: 'Read, draft, and send from your inbox with configurable approvals.', icon: Mail },
  { id: 'google-cal',  name: 'Google Calendar',    short: 'Read availability, create events, send invitations.',                icon: Calendar },
  { id: 'slack',       name: 'Slack',              short: 'Post updates, alerts, and decisions into any channel.',              icon: Hash },
  { id: 'stripe',      name: 'Stripe',             short: 'Pull transactions, reconcile payouts, flag disputes.',               icon: CreditCard },
  { id: 'dentrix',     name: 'Dentrix',            short: 'Dental practice management — appointments, billing, records.',      icon: MessageCircle },
] as const;

const CATEGORIES = ['All', 'General', 'Ops', 'Research', 'Sales', 'Content', 'Finance'];

export default function MarketplacePublicPage() {
  const [category, setCategory] = useState('All');

  const filteredAgents = useMemo(() => {
    if (category === 'All') return SHOWCASE_AGENTS;
    return SHOWCASE_AGENTS.filter(a => a.category.toLowerCase() === category.toLowerCase());
  }, [category]);

  return (
    <>
      {/* Early access banner */}
      <section className="pt-6 px-6">
        <div className="mx-auto max-w-6xl rounded-xl bg-primary-muted ghost-border px-4 py-3 text-center text-sm text-primary-bright">
          <strong className="font-semibold">Early access</strong>
          <span className="text-on-surface-variant"> — the marketplace opens fully with the waitlist tiers. </span>
          <Link href="/pricing#waitlist" className="underline font-medium hover:text-primary">Join the waitlist</Link>
          <span className="text-on-surface-variant"> or </span>
          <Link href="/#hero-chat" className="underline font-medium hover:text-primary">try the live chat</Link>.
        </div>
      </section>

      {/* Hero */}
      <section className="relative pt-14 pb-10 px-6 overflow-hidden">
        <div
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(ellipse at center, oklch(0.55 0.25 285) 0%, transparent 70%)' }}
        />
        <div className="relative mx-auto max-w-4xl">
          <p className="text-primary-bright text-xs font-bold uppercase tracking-[0.2em] mb-3">Ecosystem</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-[-0.03em] mb-4 leading-[1.05]">
            The CTRL-A <span className="hero-gradient-text">Marketplace</span>
          </h1>
          <p className="text-on-surface-variant text-lg max-w-2xl leading-relaxed">
            Agents, skills, and services for every business. Extend your autonomous operations with precision-built assets.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section className="px-6">
        <div className="mx-auto max-w-6xl flex flex-wrap gap-2">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                category === c
                  ? 'bg-primary text-white'
                  : 'bg-surface-container text-on-surface-variant hover:bg-surface-high hover:text-on-surface ghost-border'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Agents grid */}
      <section className="py-12 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredAgents.map(agent => {
              const Icon = agent.icon;
              return (
                <Link
                  key={agent.id}
                  id={`agent-${agent.id}`}
                  href={`/pricing#waitlist?interest_agent=${agent.id}`}
                  className="monolith-card group rounded-2xl bg-surface-container ghost-border p-6 hover:bg-surface-high transition-colors flex flex-col"
                >
                  <div className="size-14 rounded-xl bg-primary-muted flex items-center justify-center mb-4">
                    <Icon className="size-7 text-primary-bright" />
                  </div>
                  <h3 className="text-lg font-bold mb-1">{agent.name}</h3>
                  <p className="text-sm text-on-surface-variant mb-3">{agent.role}</p>
                  <p className="text-sm font-medium text-primary-bright mb-4 mt-auto">{agent.price}</p>
                  <div className="inline-flex items-center gap-1 text-sm text-on-surface group-hover:text-primary-bright transition-colors">
                    Reserve via waitlist <ArrowRight className="size-4" />
                  </div>
                </Link>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-on-surface-muted text-center">
            Showcase archetypes — real agents are installable once your tier is active.
          </p>
        </div>
      </section>

      {/* Skills */}
      <section className="py-14 px-6 bg-surface-container">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.02em] mb-2">Agent skills</h2>
            <p className="text-on-surface-variant">Modular capabilities that plug into your workflows.</p>
            <div className="w-12 h-1 bg-primary mt-3" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {SHOWCASE_SKILLS.map(skill => {
              const Icon = skill.icon;
              return (
                <Link
                  key={skill.id}
                  href={`/pricing#waitlist?interest_skill=${skill.id}`}
                  className="monolith-card group rounded-2xl bg-surface-high ghost-border p-6 hover:bg-surface-bright transition-colors"
                >
                  <div className="size-12 rounded-xl bg-primary-muted flex items-center justify-center mb-4">
                    <Icon className="size-6 text-primary-bright" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{skill.name}</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed">{skill.short}</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <Link href="/integrations" className="inline-flex items-center gap-1 text-sm font-medium text-primary-bright hover:text-primary">
              Browse all integrations <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 px-6">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.02em] mb-2">Partner builds</h2>
            <p className="text-on-surface-variant">Want a full website or app? Our partner team at SageAAA handles end-to-end builds with CTRL-A agents baked in.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-surface-container ghost-border p-6 flex flex-col">
              <div className="flex items-start gap-4 mb-4">
                <div className="size-12 rounded-xl bg-primary-muted flex items-center justify-center">
                  <ClipboardList className="size-6 text-primary-bright" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold">Build a Website</h3>
                  <p className="text-sm text-on-surface-variant">SageAAA builds it. CTRL-A agents come pre-integrated.</p>
                </div>
              </div>
              <p className="text-xs text-on-surface-muted mb-4">Pricing scoped per engagement — SageAAA quotes per project.</p>
              <a
                href="https://sageaaa.com?utm_source=ctrl-a&utm_medium=marketplace&utm_campaign=build-website"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto text-center bg-primary text-white font-medium py-2.5 rounded-lg hover:brightness-110 text-sm"
              >
                Inquire at SageAAA
              </a>
            </div>
            <div className="rounded-2xl bg-surface-container ghost-border p-6 flex flex-col">
              <div className="flex items-start gap-4 mb-4">
                <div className="size-12 rounded-xl bg-primary-muted flex items-center justify-center">
                  <Boxes className="size-6 text-primary-bright" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold">Build an App</h3>
                  <p className="text-sm text-on-surface-variant">SageAAA builds it. CTRL-A agents drive autonomous operations.</p>
                </div>
              </div>
              <p className="text-xs text-on-surface-muted mb-4">Pricing scoped per engagement — SageAAA quotes per project.</p>
              <a
                href="https://sageaaa.com?utm_source=ctrl-a&utm_medium=marketplace&utm_campaign=build-app"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto text-center bg-primary text-white font-medium py-2.5 rounded-lg hover:brightness-110 text-sm"
              >
                Inquire at SageAAA
              </a>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-on-surface-muted flex items-center justify-center gap-1.5">
            <Sparkles className="size-3.5 text-primary-bright" />
            Not included in any tier — separate engagement required.
          </p>
        </div>
      </section>
    </>
  );
}
